import { useState, useEffect } from "react";
import Footer from "./Footer";
import config from "../config";

const { API_BASE, UPLOADS_URL } = config;

async function fetchJSON(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status} – ${url}`);
  return res.json();
}

/* ── Gallery pool for a service: image2 → image7 ── */
function getGalleryImages(service) {
  return [
    service.image2, service.image3, service.image4,
    service.image5, service.image6, service.image7,
  ].filter(Boolean);
}

/* ── Lightbox: full-screen image viewer with pagination ── */
function Lightbox({ images, index, title, onClose, onNavigate }) {
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNavigate(1);
      if (e.key === "ArrowLeft") onNavigate(-1);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose, onNavigate]);

  return (
    <div className="pf-lightbox" onClick={onClose} role="dialog" aria-modal="true">
      <button className="pf-lightbox__close" onClick={onClose} aria-label="Close">
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
          <path d="M4 4l12 12M16 4L4 16" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>

      {images.length > 1 && (
        <button
          className="pf-lightbox__nav pf-lightbox__nav--prev"
          onClick={(e) => { e.stopPropagation(); onNavigate(-1); }}
          aria-label="Previous image"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M12 4l-6 6 6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}

      <img
        src={`${UPLOADS_URL}/services/${images[index]}`}
        alt={`${title} – ${index + 1}`}
        className="pf-lightbox__img"
        onClick={(e) => e.stopPropagation()}
      />

      {images.length > 1 && (
        <button
          className="pf-lightbox__nav pf-lightbox__nav--next"
          onClick={(e) => { e.stopPropagation(); onNavigate(1); }}
          aria-label="Next image"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M8 4l6 6-6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}

      <div className="pf-lightbox__footer">
        {title && <span className="pf-lightbox__title">{title}</span>}
        {images.length > 1 && (
          <span className="pf-lightbox__counter">{index + 1} / {images.length}</span>
        )}
      </div>
    </div>
  );
}

/* ── Single portfolio card ── */
function PortfolioCard({ service, onOpenGallery }) {
  const gallery = getGalleryImages(service);
  const hasGallery = gallery.length > 0;

  return (
    <div className="pf-card">
      <button
        type="button"
        className="pf-card__media"
        onClick={() => hasGallery && onOpenGallery(gallery, service.title)}
        aria-label={`View gallery for ${service.title}`}
        disabled={!hasGallery}
      >
        {service.image2 ? (
          <img
            src={`${UPLOADS_URL}/services/${service.image2}`}
            alt={service.title}
            className="pf-card__img"
          />
        ) : (
          <div className="pf-card__img-fallback" />
        )}

        <span className="pf-card__tag">SERVICE</span>

        {hasGallery && (
          <span className="pf-card__photos">
            <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
              <path d="M2 3a1 1 0 0 1 1-1h1.5l.7-1h5.6l.7 1H13a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3Zm6 2.2a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z" />
            </svg>
            {gallery.length} PHOTO{gallery.length > 1 ? "S" : ""}
          </span>
        )}
      </button>

      <div className="pf-card__body">
        <h3 className="pf-card__title">{service.title}</h3>
        <p className="pf-card__subtitle">{service.title.toUpperCase()}</p>

        <button
          type="button"
          className="pf-card__cta"
          onClick={() => hasGallery && onOpenGallery(gallery, service.title)}
          disabled={!hasGallery}
        >
          <span className="pf-card__cta-label">
            {hasGallery ? "View Gallery" : "No Photos Yet"}
          </span>
          {hasGallery && (
            <span className="pf-card__cta-arrow" aria-hidden="true">
              <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                <path d="M3 7h8M7 3l4 4-4 4" stroke="currentColor" strokeWidth="1.8"
                  strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          )}
        </button>
      </div>
    </div>
  );
}

/* ── Main Page ── */
function PortfolioPage() {
  const [services, setServices]   = useState([]);
  const [heroImage, setHeroImage] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  // { images: string[], title: string, index: number } | null
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchJSON(`${API_BASE}/portfolio.php`)
      .then((data) => {
        if (cancelled) return;
        setServices(Array.isArray(data?.services) ? data.services : []);
        setHeroImage(data?.hero_image || null);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("PortfolioPage load error:", err);
        setError("Failed to load portfolio.");
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  const openGallery = (images, title) => setLightbox({ images, title, index: 0 });
  const closeGallery = () => setLightbox(null);
  const navigateGallery = (dir) => {
    setLightbox((lb) => lb && ({
      ...lb,
      index: (lb.index + dir + lb.images.length) % lb.images.length,
    }));
  };

  const heroImageUrl = heroImage ? `${UPLOADS_URL}/gallery/${heroImage}` : null;

  return (
    <>
    <div className="pf">

      {/* ── HERO ── */}
      <div
        className="pf-hero"
        style={heroImageUrl ? { backgroundImage: `url(${heroImageUrl})` } : undefined}
      >
        <div className="pf-hero__overlay" aria-hidden="true" />
        <div className="pf-hero__content">
          <nav className="pf-bread" aria-label="Breadcrumb">
            <a href="/" className="pf-bread__link">Home</a>
            <span className="pf-bread__sep" aria-hidden="true">›</span>
            <span className="pf-bread__current">Portfolio</span>
          </nav>
          <h1 className="pf-hero__title">Our Portfolio</h1>
          <p className="pf-hero__subtitle">A look at the work behind every service we deliver</p>
        </div>
      </div>

      {/* ── GRID ── */}
      <div className="pf-body">
        <div className="pf-body__inner">

          {loading && <div className="pf-state">Loading portfolio…</div>}

          {!loading && error && (
            <div className="pf-state pf-state--error">{error}</div>
          )}

          {!loading && !error && services.length === 0 && (
            <div className="pf-state">No portfolio items available yet.</div>
          )}

          {!loading && !error && services.length > 0 && (
            <div className="pf-grid">
              {services.map((s) => (
                <PortfolioCard key={s.id} service={s} onOpenGallery={openGallery} />
              ))}
            </div>
          )}

        </div>
      </div>

      {lightbox && (
        <Lightbox
          images={lightbox.images}
          index={lightbox.index}
          title={lightbox.title}
          onClose={closeGallery}
          onNavigate={navigateGallery}
        />
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700;800;900&display=swap');

        .pf *, .pf *::before, .pf *::after { box-sizing: border-box; margin: 0; padding: 0; }
        .pf a { text-decoration: none; }
        .pf { font-family: 'Roboto', sans-serif; background: #f5f7fa; }

        /* ── HERO ── */
        .pf-hero {
          position: relative; padding: 56px 40px 64px;
          background-color: #013d97;
          background-image: linear-gradient(135deg, #011546 0%, #013d97 70%, #029add 140%);
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
        }
        .pf-hero__overlay {
          position: absolute; inset: 0;
          background: linear-gradient(135deg, rgba(1,21,70,.82) 0%, rgba(1,61,151,.72) 70%, rgba(2,154,221,.55) 140%);
          pointer-events: none;
        }
        .pf-hero__content { position: relative; z-index: 1; max-width: 1280px; margin: 0 auto; }
        .pf-bread { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; margin-bottom: 14px; }
        .pf-bread__link { font-size: 13px; font-weight: 600; color: rgba(255,255,255,.85); background: rgba(255,255,255,.12); padding: 3px 10px; border-radius: 3px; transition: background .2s; }
        .pf-bread__link:hover { background: rgba(255,255,255,.22); }
        .pf-bread__sep { color: rgba(255,255,255,.55); font-size: 14px; }
        .pf-bread__current { font-size: 13px; color: rgba(255,255,255,.7); }
        .pf-hero__title { font-size: clamp(26px, 3.2vw, 40px); font-weight: 900; color: #fff; line-height: 1.15; margin-bottom: 10px; }
        .pf-hero__subtitle { font-size: 15px; color: rgba(255,255,255,.78); max-width: 540px; line-height: 1.6; }

        /* ── BODY ── */
        .pf-body { padding: 48px 0 72px; }
        .pf-body__inner { max-width: 1280px; margin: 0 auto; padding: 0 40px; }

        .pf-state {
          text-align: center; padding: 64px 24px; font-size: 14px;
          color: #8890a0; background: #fff; box-shadow: 0 2px 12px rgba(0,0,0,.06);
        }
        .pf-state--error { color: #c0392b; }

        /* ── GRID ── */
        .pf-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 28px;
        }

        /* ── CARD ── */
        .pf-card {
          background: #fff; border: 1px solid #e8eaf0; border-radius: 4px;
          overflow: hidden; box-shadow: 0 2px 10px rgba(0,0,0,.05);
          transition: box-shadow .25s, transform .25s;
        }
        .pf-card:hover { box-shadow: 0 10px 26px rgba(1,21,70,.14); transform: translateY(-3px); }

        .pf-card__media {
          position: relative; display: block; width: 100%; aspect-ratio: 4/3;
          border: none; padding: 0; cursor: zoom-in; background: #e6f0fb; overflow: hidden;
        }
        .pf-card__media:disabled { cursor: default; }
        .pf-card__img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform .4s; }
        .pf-card__media:hover:not(:disabled) .pf-card__img { transform: scale(1.06); }
        .pf-card__img-fallback { width: 100%; height: 100%; background: linear-gradient(135deg, #013d97, #029add); }

        .pf-card__tag {
          position: absolute; left: 0; bottom: 0;
          background: rgba(1,21,70,.85); color: #fff;
          font-size: 11px; font-weight: 800; letter-spacing: .08em;
          padding: 7px 16px;
        }
        .pf-card__photos {
          position: absolute; top: 12px; right: 12px;
          display: flex; align-items: center; gap: 6px;
          background: #029add; color: #fff; border-radius: 3px;
          font-size: 11px; font-weight: 800; letter-spacing: .04em;
          padding: 6px 11px;
        }

        .pf-card__body { padding: 22px 24px 24px; }
        .pf-card__title { font-size: 18px; font-weight: 800; color: #1a1a2e; margin-bottom: 4px; line-height: 1.3; }
        .pf-card__subtitle { font-size: 11.5px; font-weight: 700; letter-spacing: .06em; color: #8890a0; margin-bottom: 18px; }

        .pf-card__cta {
          display: inline-flex; align-items: center; gap: 12px;
          background: none; border: none; padding: 0; cursor: pointer;
        }
        .pf-card__cta:disabled { cursor: default; opacity: .55; }
        .pf-card__cta-label { font-size: 14px; font-weight: 700; color: #1a1a2e; transition: color .2s; }
        .pf-card__cta:hover:not(:disabled) .pf-card__cta-label { color: #029add; }
        .pf-card__cta-arrow {
          width: 32px; height: 32px; flex-shrink: 0; border-radius: 3px;
          background: #029add; color: #fff;
          display: flex; align-items: center; justify-content: center;
          transition: background .2s;
        }
        .pf-card__cta:hover:not(:disabled) .pf-card__cta-arrow { background: #013d97; }

        /* ── LIGHTBOX ── */
        .pf-lightbox {
          position: fixed; inset: 0; z-index: 1000;
          background: rgba(5,10,25,.92);
          display: flex; align-items: center; justify-content: center; padding: 40px;
        }
        .pf-lightbox__img {
          max-width: min(90vw, 1100px); max-height: 82vh;
          object-fit: contain; border-radius: 4px;
          box-shadow: 0 10px 40px rgba(0,0,0,.5);
        }
        .pf-lightbox__close {
          position: absolute; top: 20px; right: 20px;
          width: 40px; height: 40px; border-radius: 50%;
          background: rgba(255,255,255,.12); border: none; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: background .2s;
        }
        .pf-lightbox__close:hover { background: rgba(255,255,255,.25); }
        .pf-lightbox__nav {
          position: absolute; top: 50%; transform: translateY(-50%);
          width: 48px; height: 48px; border-radius: 50%;
          background: rgba(255,255,255,.12); border: none; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: background .2s;
        }
        .pf-lightbox__nav:hover { background: rgba(255,255,255,.25); }
        .pf-lightbox__nav--prev { left: 20px; }
        .pf-lightbox__nav--next { right: 20px; }
        .pf-lightbox__footer {
          position: absolute; bottom: 24px; left: 50%; transform: translateX(-50%);
          display: flex; align-items: center; gap: 14px;
          color: #fff; font-size: 13px;
        }
        .pf-lightbox__title { font-weight: 700; }
        .pf-lightbox__counter { background: rgba(255,255,255,.14); padding: 6px 14px; border-radius: 20px; }

        /* ── RESPONSIVE ── */
        @media (max-width: 768px) {
          .pf-hero { padding: 40px 20px 48px; }
          .pf-body__inner { padding: 0 20px; }
          .pf-grid { grid-template-columns: 1fr; gap: 20px; }
          .pf-lightbox { padding: 20px; }
          .pf-lightbox__nav { width: 40px; height: 40px; }
        }

        a:focus-visible, button:focus-visible { outline: 2px solid #029add; outline-offset: 2px; }
        @media (prefers-reduced-motion: reduce) {
          .pf *, .pf *::before, .pf *::after { transition-duration: 0ms !important; }
        }
      `}</style>
    </div>
    <Footer />
    </>
  );
}

export default PortfolioPage;